import { AnyAction } from 'redux'
import { ActionType } from 'redux-promise-middleware'

import {
  FETCH_ENERGY_DISTRIBUTION_FULFILLED,
  FETCH_ENERGY_DISTRIBUTION_PENDING,
  FETCH_ENERGY_HISTORY_FULFILLED,
  FETCH_ENERGY_HISTORY_PENDING,
  FETCH_NUTRITION_SUMMARY_FULFILLED,
  FETCH_NUTRITION_SUMMARY_PENDING,
  PATCH_BODY_MEASUREMENTS_FULFILLED,
  POST_ANAMNESIS_FULFILLED,
  POST_BODY_MEASUREMENTS_FULFILLED,
  POST_PROGRAM_FULFILLED,
  RESET_GOAL_NUTRITIONS,
  UPDATE_ANAMNESIS_FULFILLED,
  UPDATE_USER_FULFILLED
} from './actionTypes'

import {
  AnalyticsCacheEntry,
  AnalyticsCacheName,
  ByIdMap,
  DateRange,
  MyAnalyticsCache,
  MyAnalyticsCacheData,
  ThunkAction
} from 'Models'
import { coreBackendService } from 'Services'

export const ANALYTICS_CACHE_MAX_AGE_MS = 600 * 1000
export const ANALYTICS_CACHE_MAX_ENTRIES = 10
export const SET_ACTIVE_ANALYTICS_CACHE_KEY = 'SET_ACTIVE_ANALYTICS_CACHE_KEY'

type AnalyticsCacheMeta = {
  analyticsCacheKey?: string
  analyticsCacheName?: AnalyticsCacheName
  analyticsCacheRevision?: number
  analyticsCacheTimestamp?: number
  skipLoadingIndicator?: boolean
}

export interface AnalyticsCacheAction extends AnyAction {
  meta?: AnalyticsCacheMeta
}

const analyticsPendingActions = [
  FETCH_NUTRITION_SUMMARY_PENDING,
  FETCH_ENERGY_HISTORY_PENDING,
  FETCH_ENERGY_DISTRIBUTION_PENDING
]

const analyticsFulfilledActions = [
  FETCH_NUTRITION_SUMMARY_FULFILLED,
  FETCH_ENERGY_HISTORY_FULFILLED,
  FETCH_ENERGY_DISTRIBUTION_FULFILLED
]

const journalMutationPattern = new RegExp(`^(ADD|REMOVE|UPDATE)_JOURNAL_(?!NOTES_).+_${ActionType.Fulfilled}$`)

const analyticsGoalInvalidationActions = new Set([
  RESET_GOAL_NUTRITIONS,
  POST_ANAMNESIS_FULFILLED,
  UPDATE_ANAMNESIS_FULFILLED,
  POST_BODY_MEASUREMENTS_FULFILLED,
  PATCH_BODY_MEASUREMENTS_FULFILLED,
  POST_PROGRAM_FULFILLED,
  UPDATE_USER_FULFILLED
])

const shouldInvalidateAnalyticsCache = (type: string): boolean =>
  journalMutationPattern.test(type) || analyticsGoalInvalidationActions.has(type)

export const initialCache: MyAnalyticsCache = {
  revision: 0,
  activeKeys: {},
  nutritionSummary: {},
  energyHistory: {},
  energyDistribution: {}
}

const createAnalyticsCacheKey = ({ dateFrom, dateTo }: DateRange): string =>
  `${coreBackendService.userId || 'anonymous'}:${dateFrom.getTime()}:${dateTo.getTime()}`

const hasNewerOrEqualAnalyticsCacheEntry = (existingFetchedAt: number | undefined, fetchedAt: number): boolean =>
  existingFetchedAt !== undefined && existingFetchedAt >= fetchedAt

const setAnalyticsCacheEntry = <T>(
  entries: ByIdMap<AnalyticsCacheEntry<T>>,
  cacheKey: string,
  cacheEntry: AnalyticsCacheEntry<T>
): ByIdMap<AnalyticsCacheEntry<T>> => {
  if (hasNewerOrEqualAnalyticsCacheEntry(entries[cacheKey]?.fetchedAt, cacheEntry.fetchedAt)) {
    return entries
  }

  const candidateEntries = {
    ...entries,
    [cacheKey]: cacheEntry
  }
  const newestFetchedAt = Object.values(candidateEntries).reduce(
    (newest, entry) => Math.max(newest, entry.fetchedAt),
    cacheEntry.fetchedAt
  )

  return Object.entries(candidateEntries)
    .filter(([, entry]) => newestFetchedAt - entry.fetchedAt < ANALYTICS_CACHE_MAX_AGE_MS)
    .sort(([, entryA], [, entryB]) => entryB.fetchedAt - entryA.fetchedAt)
    .slice(0, ANALYTICS_CACHE_MAX_ENTRIES)
    .reduce<ByIdMap<AnalyticsCacheEntry<T>>>(
      (result, [key, entry]) => ({
        ...result,
        [key]: entry
      }),
      {}
    )
}

export const fetchAnalyticsWithCache = <K extends AnalyticsCacheName>(
  range: DateRange,
  cacheName: K,
  actionType: string,
  fetchFn: () => Promise<MyAnalyticsCacheData[K]>
): ThunkAction => (dispatch, getState) => {
  const cacheKey = createAnalyticsCacheKey(range)
  const { cache } = getState().myAnalytics
  const cacheEntry = cache[cacheName][cacheKey]
  const now = Date.now()

  if (cacheEntry && now - cacheEntry.fetchedAt < ANALYTICS_CACHE_MAX_AGE_MS) {
    dispatch({
      type: SET_ACTIVE_ANALYTICS_CACHE_KEY,
      meta: {
        analyticsCacheKey: cacheKey,
        analyticsCacheName: cacheName
      }
    })
    return
  }

  dispatch({
    type: actionType,
    payload: fetchFn(),
    meta: {
      analyticsCacheKey: cacheKey,
      analyticsCacheName: cacheName,
      analyticsCacheRevision: cache.revision,
      analyticsCacheTimestamp: now,
      skipLoadingIndicator: !!cacheEntry
    }
  })
}

export const analyticsCacheReducer = (
  state: MyAnalyticsCache = initialCache,
  { type, payload, meta }: AnalyticsCacheAction
): MyAnalyticsCache => {
  if (shouldInvalidateAnalyticsCache(type)) {
    return {
      ...initialCache,
      revision: state.revision + 1,
      activeKeys: state.activeKeys
    }
  }

  const cacheName = meta?.analyticsCacheName
  const cacheKey = meta?.analyticsCacheKey

  if (cacheName && cacheKey && (type === SET_ACTIVE_ANALYTICS_CACHE_KEY || analyticsPendingActions.includes(type))) {
    return {
      ...state,
      activeKeys: {
        ...state.activeKeys,
        [cacheName]: cacheKey
      }
    }
  }

  if (
    !cacheKey ||
    !analyticsFulfilledActions.includes(type) ||
    meta?.analyticsCacheRevision !== state.revision ||
    meta?.analyticsCacheTimestamp === undefined
  ) {
    return state
  }

  const cacheEntry = {
    data: payload,
    fetchedAt: meta.analyticsCacheTimestamp
  }

  switch (type) {
    case FETCH_NUTRITION_SUMMARY_FULFILLED:
      return {
        ...state,
        nutritionSummary: setAnalyticsCacheEntry(state.nutritionSummary, cacheKey, cacheEntry)
      }
    case FETCH_ENERGY_HISTORY_FULFILLED:
      return {
        ...state,
        energyHistory: setAnalyticsCacheEntry(state.energyHistory, cacheKey, cacheEntry)
      }
    case FETCH_ENERGY_DISTRIBUTION_FULFILLED:
      return {
        ...state,
        energyDistribution: setAnalyticsCacheEntry(state.energyDistribution, cacheKey, cacheEntry)
      }
    default:
      return state
  }
}

export const getActiveAnalyticsCacheEntry = <K extends AnalyticsCacheName>(
  cache: MyAnalyticsCache,
  cacheName: K
): AnalyticsCacheEntry<MyAnalyticsCacheData[K]> | undefined => {
  const activeKey = cache.activeKeys[cacheName]
  if (!activeKey) {
    return undefined
  }

  return (cache[cacheName] as ByIdMap<AnalyticsCacheEntry<MyAnalyticsCacheData[K]>>)[activeKey as string]
}

export const getDisplayedAnalyticsCacheEntry = <K extends AnalyticsCacheName>(
  cache: MyAnalyticsCache,
  cacheName: K
): AnalyticsCacheEntry<MyAnalyticsCacheData[K]> | undefined => {
  const activeEntry = getActiveAnalyticsCacheEntry(cache, cacheName)

  if (activeEntry) {
    return activeEntry
  }

  return Object.values(cache[cacheName] as ByIdMap<AnalyticsCacheEntry<MyAnalyticsCacheData[K]>>).reduce<
    AnalyticsCacheEntry<MyAnalyticsCacheData[K]> | undefined
  >((newest, entry) => {
    if (!newest || entry.fetchedAt > newest.fetchedAt) {
      return entry
    }

    return newest
  }, undefined)
}

export const hasActiveAnalyticsCacheEntry = (cache: MyAnalyticsCache, cacheName: AnalyticsCacheName): boolean =>
  !!getActiveAnalyticsCacheEntry(cache, cacheName)
